import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, Textarea, Wrap, WrapItem, Select,
  Divider, IconButton, Tooltip, AlertDialog, AlertDialogBody, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Spinner,
  Menu, MenuList, MenuItem,
} from '@chakra-ui/react';
import {
  FiRefreshCw, FiMail, FiSend, FiCornerUpLeft, FiPaperclip, FiFile,
  FiSliders, FiCheck, FiEye, FiCheckCircle, FiArrowLeft, FiPlus, FiX, FiClock,
} from 'react-icons/fi';
import api from '../api';
import useMenuPosition from '../hooks/useMenuPosition';

const STATUS_CONFIG = {
  shown:        { label: 'New',          color: 'blue'   },
  viewed:       { label: 'Viewed',       color: 'orange' },
  acknowledged: { label: 'Acknowledged', color: 'green'  },
};
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.shown;
  return <Badge colorScheme={cfg.color} fontSize="8px" variant="subtle">{cfg.label}</Badge>;
}

export default function InboxModal({ isOpen, onClose, onRefreshCounts, onNavigateToProject }) {
  const [view, setView] = useState('inbox'); // inbox | email | rules
  const [emails, setEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showReply, setShowReply] = useState(false);
  const [replyForm, setReplyForm] = useState({ subject: '', body: '' });
  const [replyFiles, setReplyFiles] = useState([]);
  const replyFileRef = useRef(null);
  const [syncing, setSyncing] = useState(false);
  const [sending, setSending] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { emailId, isAssigned, x, y }
  // Keep right-click menus inside the viewport — see hooks/useMenuPosition.
  const contextMenuRef = useRef(null);
  const contextMenuPos = useMenuPosition(contextMenuRef, contextMenu);
  const [applyAllPromptOpen, setApplyAllPromptOpen] = useState(false);
  const [applyAllPromptRef] = useState({ current: null });
  const [applyingAll, setApplyingAll] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFrom, setHistoryFrom] = useState('');
  const [historyTo, setHistoryTo] = useState('');
  const [historyEmails, setHistoryEmails] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const toast = useToast();

  // Assign state
  const [assignLoading, setAssignLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [assignCompany, setAssignCompany] = useState('');
  const [assignProject, setAssignProject] = useState('');
  const [assignTask, setAssignTask] = useState('');
  const [assignContactInfo, setAssignContactInfo] = useState(null);
  const [assigning, setAssigning] = useState(false);

  // Rule prompt
  const rulePromptRef = useRef();
  const [rulePromptOpen, setRulePromptOpen] = useState(false);
  const [pendingAssign, setPendingAssign] = useState(null);

  // Rules management
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [newRule, setNewRule] = useState({ name: '', match_type: 'from_address', match_value: '', target_task_id: '', target_project_id: '' });
  const [savingRule, setSavingRule] = useState(false);

  const fetchHistory = async () => {
    if (!historyFrom || !historyTo) return;
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/email/history?from=${historyFrom}&to=${historyTo}`);
      setHistoryEmails(data);
    } catch(e) { toast({ title: 'Failed to load history', status: 'error', duration: 2000 }); }
    setHistoryLoading(false);
  };

    const fetchEmails = useCallback(async (hidden) => {
    const h = hidden !== undefined ? hidden : showHidden;
    try { const { data } = await api.get('/email/inbox' + (h ? '?show_hidden=true' : '')); setEmails(data); } catch(e) {}
  }, [showHidden]);

  const fetchRules = useCallback(async () => {
    setRulesLoading(true);
    try { const { data } = await api.get('/email/rules'); setRules(data); } catch(e) {}
    finally { setRulesLoading(false); }
  }, []);

  useEffect(() => {
    if (isOpen) { fetchEmails(); setSelected(null); setView('inbox'); }
  }, [isOpen, fetchEmails]);

  // Load projects when company manually changed in dropdown
  const handleCompanyChange = async (companyId) => {
    setAssignCompany(companyId);
    setAssignProject(''); setAssignTask(''); setProjects([]); setTasks([]);
    if (!companyId) return;
    try {
      const r = await api.get('/email/assign-lookup?company_id=' + companyId);
      setProjects(r.data.projects || []);
    } catch(e) {}
  };

  // Load tasks when project manually changed
  const handleProjectChange = async (projectId) => {
    setAssignProject(projectId);
    setAssignTask(''); setTasks([]);
    if (!projectId) return;
    try {
      const r = await api.get('/email/assign-lookup?project_id=' + projectId);
      setTasks(r.data.tasks || []);
    } catch(e) {}
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/email/sync-all');
      toast({ title: 'Email Sync Started', description: `Syncing ${data.accounts || 'all'} account(s) in background`, status: 'info', duration: 3000 });
      setTimeout(async () => { await fetchEmails(); if (onRefreshCounts) onRefreshCounts(); }, 5000);
    } catch(e) {
      toast({ title: 'Email Sync Failed', description: e.response?.data?.error || 'Connection error', status: 'error', duration: 4000 });
    } finally { setSyncing(false); }
  };

  const openEmail = async (email) => {
    setAssignLoading(true);
    setAssignCompany(''); setAssignProject(''); setAssignTask('');
    setAssignContactInfo(null);
    setCompanies([]); setProjects([]); setTasks([]);
    try {
      const { data } = await api.get('/email/messages/' + email.id);
      setSelected(data);
      setShowReply(false);
      setView('email');

      // Load matched companies from sender address in one call
      if (data.from_address) {
        try {
          const r = await api.get('/email/assign-lookup?from_address=' + encodeURIComponent(data.from_address));
          const matchedCompanies = r.data.matchedCompanies || [];
          const contactInfo = r.data.contactInfo;

          if (matchedCompanies.length > 0) {
            setCompanies(matchedCompanies);
            setAssignContactInfo(r.data.contactInfo || null);
            // Auto-select if only one company
            if (matchedCompanies.length === 1) {
              const cid = matchedCompanies[0].id;
              setAssignCompany(cid);
              // Immediately load projects for this company
              const r2 = await api.get('/email/assign-lookup?company_id=' + cid);
              setProjects(r2.data.projects || []);
            }
          } else if (contactInfo && contactInfo.company_id) {
            // Fallback: single company from contactInfo
            const fallback = [{ id: contactInfo.company_id, name: contactInfo.company_name }];
            setCompanies(fallback);
            setAssignCompany(contactInfo.company_id);
            const r2 = await api.get('/email/assign-lookup?company_id=' + contactInfo.company_id);
            setProjects(r2.data.projects || []);
          } else {
            // No contact found — show searchable full list
            setCompanies(r.data.companies || []);
          }
        } catch(e) {}
      }

      await fetchEmails();
      if (onRefreshCounts) onRefreshCounts();
    } catch(e) {}
    finally { setAssignLoading(false); }
  };

  const handleAssign = async () => {
    if (!selected || !assignTask) return;
    setAssigning(true);
    try {
      await api.put('/email/messages/' + selected.id + '/assign', { task_id: assignTask, project_id: assignProject });
      setPendingAssign({ task_id: assignTask, project_id: assignProject, from_address: selected.from_address });
      setRulePromptOpen(true);
      setSelected(s => ({ ...s, is_assigned: true }));
      await fetchEmails();
      if (onRefreshCounts) onRefreshCounts();
    } catch(e) { toast({ title: 'Assign failed', status: 'error', duration: 2000 }); }
    finally { setAssigning(false); }
  };

  const handleCreateRule = async () => {
    if (!pendingAssign) return;
    try {
      await api.post('/email/rules', {
        name: 'Auto: ' + pendingAssign.from_address,
        match_type: 'from_address',
        match_value: pendingAssign.from_address,
        target_task_id: pendingAssign.task_id,
        target_project_id: pendingAssign.project_id,
      });
      toast({ title: 'Filter rule saved', status: 'success', duration: 2000 });
    } catch(e) { toast({ title: 'Could not save rule', status: 'error', duration: 2000 }); }
    setRulePromptOpen(false);
    // Now ask whether to apply rule to all existing inbox emails
    setApplyAllPromptOpen(true);
  };

  const handleApplyAll = async () => {
    setApplyingAll(true);
    try {
      const { data } = await api.post('/email/rules/apply-all');
      toast({ title: `Applied to inbox: ${data.applied} email(s) assigned`, status: 'success', duration: 3000 });
      await fetchEmails();
      if (onRefreshCounts) onRefreshCounts();
    } catch(e) { toast({ title: 'Apply failed', status: 'error', duration: 2000 }); }
    setApplyingAll(false);
    setApplyAllPromptOpen(false);
    if (onNavigateToProject && pendingAssign?.project_id) { onClose(); onNavigateToProject(pendingAssign.project_id, null, pendingAssign.task_id); }
    setPendingAssign(null);
  };

  const handleApplyAllSkip = () => {
    setApplyAllPromptOpen(false);
    if (onNavigateToProject && pendingAssign?.project_id) { onClose(); onNavigateToProject(pendingAssign.project_id, null, pendingAssign.task_id); }
    setPendingAssign(null);
  };

  const handleHideEmail = async (emailId) => {
    try {
      await api.patch('/email/messages/' + emailId + '/hide', { hidden: true });
      await fetchEmails();
      if (onRefreshCounts) onRefreshCounts();
    } catch(e) { toast({ title: 'Failed to hide', status: 'error', duration: 2000 }); }
    setContextMenu(null);
  };

  const handleDeleteEmail = async (emailId) => {
    try {
      await api.delete('/email/messages/' + emailId);
      await fetchEmails();
      if (onRefreshCounts) onRefreshCounts();
      toast({ title: 'Message deleted', status: 'info', duration: 1500 });
    } catch(e) { toast({ title: e.response?.data?.error || 'Delete failed', status: 'error', duration: 2500 }); }
    setContextMenu(null);
  };

  const handleSkipRule = () => {
    const proj = pendingAssign;
    setRulePromptOpen(false);
    setPendingAssign(null);
    if (onNavigateToProject && proj && proj.project_id) { onClose(); onNavigateToProject(proj.project_id); }
  };

  const handleAcknowledge = async (emailId, e) => {
    if (e) e.stopPropagation();
    try {
      await api.patch('/email/messages/' + emailId + '/status', { status: 'acknowledged' });
      // Update locally without re-fetching — avoid the email disappearing
      setEmails(prev => prev.map(em => em.id === emailId ? { ...em, inbox_status: 'acknowledged' } : em));
      if (onRefreshCounts) onRefreshCounts();
    } catch(e) {}
  };

  const handleReply = async () => {
    if (!selected || !replyForm.body) return;
    setSending(true);
    try {
      const fileIds = [];
      for (const file of replyFiles) {
        try {
          const fd = new FormData(); fd.append('file', file); fd.append('folder', 'communications');
          fd.append('entity_type', 'email'); fd.append('entity_id', '0');
          const { data: f2 } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          fileIds.push(f2.id);
        } catch(e) {}
      }
      await api.post('/email/send', {
        account_id: selected.email_account_id, to: selected.from_address,
        subject: replyForm.subject || 'Re: ' + selected.subject,
        html: replyForm.body.replace(/\n/g, '<br>'), text: replyForm.body,
        in_reply_to: selected.message_id,
        attachment_file_ids: fileIds.length > 0 ? fileIds : undefined,
      });
      toast({ title: 'Reply sent', status: 'success', duration: 1500 });
      setShowReply(false); setReplyForm({ subject: '', body: '' }); setReplyFiles([]);
    } catch(e) { toast({ title: e.response?.data?.error || 'Send failed', status: 'error', duration: 3000 }); }
    finally { setSending(false); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '';

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="80vh" mb="60px" overflowX="hidden"
        sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
        <ModalHeader fontSize="md" pb={2}>
          <Flex justify="space-between" align="center" pr={8}>
            <HStack spacing={2}>
              <FiMail />
              <Text>Email Inbox</Text>
              <Badge colorScheme="blue" fontSize="10px">
                {emails.filter(e => !e.inbox_status || e.inbox_status === 'shown').length} new
              </Badge>
            </HStack>
            <HStack spacing={1}>
              <Tooltip label="Filter Rules">
                <IconButton icon={<FiSliders size={15} />} size="xs" variant="ghost"
                  colorScheme={view === 'rules' ? 'blue' : 'gray'}
                  onClick={() => { if (view !== 'rules') { setView('rules'); fetchRules(); } else setView('inbox'); }}
                  aria-label="Rules" />
              </Tooltip>
              <Tooltip label={showHidden ? 'Hide hidden messages' : 'Show hidden messages'}>
                <IconButton icon={<FiEye size={15} />} size="xs" variant={showHidden ? 'solid' : 'ghost'}
                  colorScheme={showHidden ? 'orange' : 'gray'}
                  onClick={() => { const next = !showHidden; setShowHidden(next); fetchEmails(next); }}
                  aria-label="Show hidden" />
              </Tooltip>
              <Tooltip label="Show old messages by date range">
                <IconButton icon={<FiClock size={15} />} size="xs" variant="ghost" colorScheme="gray"
                  onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    const month = new Date(Date.now() - 30*86400000).toISOString().split('T')[0];
                    setHistoryFrom(month); setHistoryTo(today); setHistoryOpen(true);
                  }}
                  aria-label="Show old messages" />
              </Tooltip>
              <Button size="xs" leftIcon={<FiRefreshCw />} colorScheme="blue" variant="outline"
                onClick={handleSyncAll} isLoading={syncing}>Sync All</Button>
            </HStack>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" pb={4}>

          {/* ── RULES VIEW ── */}
          {view === 'rules' && (
            <Box>
              <HStack mb={3}>
                <Button size="xs" variant="ghost" leftIcon={<FiArrowLeft />} onClick={() => setView('inbox')}>Back</Button>
                <Text fontSize="sm" fontWeight="700">Email Filter Rules</Text>
              </HStack>
              {rulesLoading ? <Spinner size="sm" /> : (
                <VStack spacing={2} align="stretch" mb={4}>
                  {rules.length === 0 && <Text fontSize="sm" color="gray.400" textAlign="center" py={4}>No rules yet.</Text>}
                  {rules.map(r => (
                    <Flex key={r.id} p={2} border="1px" borderColor="gray.200" borderRadius="md" align="center" justify="space-between">
                      <Box>
                        <Text fontSize="12px" fontWeight="600">{r.name}</Text>
                        <Text fontSize="10px" color="gray.500">{r.match_type}: <strong>{r.match_value}</strong>
                          {r.project_name && ' → ' + r.project_name}{r.task_title && ' / ' + r.task_title}</Text>
                      </Box>
                      <HStack spacing={1}>
                        <Badge colorScheme={r.is_active ? 'green' : 'gray'} fontSize="8px">{r.is_active ? 'Active' : 'Off'}</Badge>
                        <IconButton size="xs" icon={<FiX />} colorScheme="red" variant="ghost"
                          onClick={async () => { await api.delete('/email/rules/' + r.id); fetchRules(); }} aria-label="Delete" />
                      </HStack>
                    </Flex>
                  ))}
                </VStack>
              )}
              <Divider mb={3} />
              <Text fontSize="11px" fontWeight="700" color="gray.600" mb={2}>Add Rule</Text>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Rule Name</FormLabel>
                    <Input size="xs" value={newRule.name} onChange={e => setNewRule(p => ({ ...p, name: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Match Type</FormLabel>
                    <Select size="xs" value={newRule.match_type} onChange={e => setNewRule(p => ({ ...p, match_type: e.target.value }))}>
                      <option value="from_address">From Address</option>
                      <option value="from_domain">From Domain</option>
                      <option value="subject_contains">Subject Contains</option>
                    </Select></FormControl>
                </HStack>
                <FormControl w="100%"><FormLabel fontSize="xs" mb={0.5}>Match Value</FormLabel>
                  <Input size="xs" value={newRule.match_value} placeholder="e.g. person@company.com"
                    onChange={e => setNewRule(p => ({ ...p, match_value: e.target.value }))} /></FormControl>
                <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" alignSelf="flex-end" isLoading={savingRule}
                  onClick={async () => {
                    if (!newRule.name || !newRule.match_value) return;
                    setSavingRule(true);
                    try { await api.post('/email/rules', newRule); toast({ title: 'Rule saved', status: 'success', duration: 1500 }); setNewRule({ name: '', match_type: 'from_address', match_value: '', target_task_id: '', target_project_id: '' }); fetchRules(); }
                    catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
                    finally { setSavingRule(false); }
                  }}>Add Rule</Button>
              </VStack>
            </Box>
          )}

          {/* ── EMAIL DETAIL ── */}
          {view === 'email' && selected && (
            <Box>
              <HStack mb={2} justify="space-between">
                <Button size="xs" variant="ghost" leftIcon={<FiArrowLeft />} onClick={() => setView('inbox')}>Back</Button>
                <HStack spacing={1}>
                  <StatusBadge status={selected.inbox_status} />
                  {selected.inbox_status !== 'acknowledged' && (
                    <Button size="xs" leftIcon={<FiCheckCircle size={11} />} colorScheme="green" variant="outline"
                      onClick={e => handleAcknowledge(selected.id, e)}>Acknowledge</Button>
                  )}
                </HStack>
              </HStack>
              <Box bg="gray.50" p={3} borderRadius="md" mb={3}>
                <Text fontSize="13px" fontWeight="700">{selected.subject}</Text>
                <HStack fontSize="10px" color="gray.500" spacing={2} mt={1} flexWrap="wrap">
                  <Text>From: <strong>{selected.from_name || selected.from_address}</strong></Text>
                  {selected.from_name && selected.from_name !== selected.from_address && <Text color="gray.400">&lt;{selected.from_address}&gt;</Text>}
                  <Text>{fmtDate(selected.received_at)}</Text>
                </HStack>
                {selected.contact_first_name && <Badge fontSize="8px" colorScheme="green" mt={1}>Contact: {selected.contact_first_name} {selected.contact_last_name || ''}</Badge>}
              </Box>
              <Box border="1px" borderColor="gray.200" borderRadius="md" p={3} mb={3} maxH="280px" overflowY="auto" overflowX="auto" bg="white">
                {selected.body_html
                  ? <Box fontSize="12px" dangerouslySetInnerHTML={{ __html: selected.body_html }}
                      sx={{
                        'img': { maxW: '100%' }, 'a': { color: 'blue.500' },
                        // Outlook HTML emails carry fixed-width (600px+)
                        // layout tables; cap them to the box so they wrap or
                        // scroll here rather than stretching the modal off
                        // the screen edge.
                        'table': { maxWidth: '100% !important', width: 'auto !important' },
                        'td, th, p, div, span': { wordBreak: 'break-word', overflowWrap: 'anywhere' },
                      }} />
                  : <Text fontSize="12px" whiteSpace="pre-wrap" overflowWrap="anywhere">{String(selected.body_text || '(empty)').replace(/\u00A0/g, ' ')}</Text>}
              </Box>
              {selected.attachments && selected.attachments.length > 0 && (
                <Box mb={3}>
                  <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color="gray.500" mb={1}>Attachments</Text>
                  <Wrap spacing={1}>
                    {selected.attachments.map(f => (
                      <WrapItem key={f.id}>
                        <Badge fontSize="9px" colorScheme="gray" cursor="pointer" display="flex" alignItems="center" gap={1}
                          onClick={() => window.open('/api/files/' + f.folder + '/' + f.stored_name, '_blank')}>
                          <FiFile size={10} /> {f.original_name}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              )}
              <HStack mb={3}>
                <Button size="xs" leftIcon={<FiCornerUpLeft />} colorScheme="blue" variant="outline"
                  onClick={() => { setShowReply(!showReply); setReplyForm({ subject: 'Re: ' + selected.subject, body: '' }); }}>Reply</Button>
              </HStack>
              {showReply && (
                <Box bg="blue.50" p={3} borderRadius="md" mb={3}>
                  <VStack spacing={2}>
                    <Text fontSize="10px" color="gray.500">Replying to {selected.from_address}</Text>
                    <FormControl><FormLabel fontSize="xs" mb={0.5}>Subject</FormLabel>
                      <Input size="xs" value={replyForm.subject} onChange={e => setReplyForm(p => ({ ...p, subject: e.target.value }))} /></FormControl>
                    <FormControl><FormLabel fontSize="xs" mb={0.5}>Message</FormLabel>
                      <Textarea size="sm" rows={4} value={replyForm.body} onChange={e => setReplyForm(p => ({ ...p, body: e.target.value }))} /></FormControl>
                    <Flex align="center" gap={2} w="100%">
                      <Button size="xs" leftIcon={<FiPaperclip />} variant="ghost" onClick={() => replyFileRef.current && replyFileRef.current.click()}>Attach</Button>
                      <input type="file" ref={replyFileRef} style={{ display:'none' }} multiple
                        onChange={e => setReplyFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                      {replyFiles.length > 0 && <Text fontSize="9px" color="gray.500">{replyFiles.length} file(s)</Text>}
                      <Button size="xs" leftIcon={<FiSend />} bg="brand.600" color="white" _hover={{ bg:'brand.700' }}
                        onClick={handleReply} isLoading={sending} ml="auto">Send Reply</Button>
                    </Flex>
                  </VStack>
                </Box>
              )}
              {!selected.is_assigned ? (
                <Box bg="orange.50" p={3} borderRadius="md" border="1px" borderColor="orange.200">
                  <Text fontSize="11px" fontWeight="700" color="orange.700" mb={3}>Assign to Project / Task</Text>
                  {assignLoading ? <Spinner size="sm" /> : (
                    <VStack spacing={2}>
                      {/* Company — auto-selected if 1 match, dropdown if multiple, search if none */}
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>
                          Company
                          {companies.length === 1 && <Badge ml={2} colorScheme="green" fontSize="8px">Matched from sender</Badge>}
                          {companies.length > 1 && <Badge ml={2} colorScheme="blue" fontSize="8px">{companies.length} matches</Badge>}
                          {companies.length === 0 && <Badge ml={2} colorScheme="gray" fontSize="8px">No contact found</Badge>}
                        </FormLabel>
                        {companies.length === 1 ? (
                          <Box bg="white" border="1px" borderColor="gray.200" borderRadius="md" px={3} py={2}>
                            <Text fontSize="sm" fontWeight="600">{companies[0].name}</Text>
                            {assignContactInfo && (
                              <Text fontSize="10px" color="gray.500" mt={0.5}>
                                Contact: {assignContactInfo.first_name} {assignContactInfo.last_name || ''} · {assignContactInfo.email}
                              </Text>
                            )}
                            <Text fontSize="9px" color="orange.400" mt={0.5}>
                              ⚠ If this company looks wrong, update the contact record and re-open this email.
                            </Text>
                          </Box>
                        ) : (
                          <Select size="xs" placeholder={companies.length === 0 ? 'No contact found for this sender' : 'Choose company…'}
                            value={assignCompany} onChange={e => handleCompanyChange(e.target.value)}
                            isDisabled={companies.length === 0}>
                            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </Select>
                        )}
                      </FormControl>
                      {assignCompany && (
                        <FormControl>
                          <FormLabel fontSize="xs" mb={0.5}>Select Project</FormLabel>
                          <Select size="xs" placeholder={projects.length === 0 ? 'No projects for this company' : 'Choose project…'}
                            value={assignProject} onChange={e => handleProjectChange(e.target.value)}
                            isDisabled={projects.length === 0}>
                            {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                          </Select>
                          {projects.length === 0 && <Text fontSize="10px" color="gray.400" mt={1}>This company has no active projects.</Text>}
                        </FormControl>
                      )}
                      {assignProject && (
                        <FormControl>
                          <FormLabel fontSize="xs" mb={0.5}>Select Task</FormLabel>
                          <Select size="xs" placeholder={tasks.length === 0 ? 'No tasks for this project' : 'Choose task…'}
                            value={assignTask} onChange={e => setAssignTask(e.target.value)}
                            isDisabled={tasks.length === 0}>
                            {tasks.map(t => (
                              <option key={t.id} value={t.id}>
                                {t.is_complete ? '✓ ' : t.is_held ? '⏸ ' : ''}{t.task_title}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      <Button size="xs" bg="brand.600" color="white" _hover={{ bg:'brand.700' }}
                        onClick={handleAssign} isLoading={assigning} isDisabled={!assignTask} alignSelf="flex-end">
                        Assign to Task
                      </Button>
                    </VStack>
                  )}
                </Box>
              ) : (
                <Box bg="green.50" p={2} borderRadius="md" border="1px" borderColor="green.200">
                  <HStack><FiCheck color="green" /><Text fontSize="11px" color="green.700">Assigned to project</Text></HStack>
                </Box>
              )}
            </Box>
          )}

          {/* ── INBOX LIST ── */}
          {view === 'inbox' && (
            emails.length === 0
              ? <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>No messages. Click Sync to check.</Text>
              : (
                <VStack spacing={1} align="stretch">
                  {emails.map(em => {
                    const isNew = !em.is_read && (em.inbox_status === 'shown' || !em.inbox_status);
                    const isAcked = em.inbox_status === 'acknowledged';
                    const isAssignedUnseen = !!em.is_assigned;
                    const isUnread = isNew || (isAssignedUnseen && !em.is_read);
                    return (
                      <Flex key={em.id} p={2} border="1px" borderRadius="md"
                        borderColor={isAssignedUnseen ? 'purple.300' : isUnread ? 'yellow.400' : em.inbox_status === 'viewed' ? 'orange.200' : 'gray.100'}
                        bg={isAssignedUnseen ? 'purple.50' : isUnread ? 'yellow.50' : em.inbox_status === 'viewed' ? 'orange.50' : 'white'}
                        onContextMenu={e => { e.preventDefault(); setContextMenu({ emailId: em.id, isAssigned: !!em.is_assigned, isHidden: !!em.is_inbox_hidden, x: e.clientX, y: e.clientY }); }}
                        _hover={{ bg: 'gray.100' }} align="center" justify="space-between" gap={1}>

                        {/* Hide checkbox */}
                        <Tooltip label={em.is_inbox_hidden ? 'Unhide from inbox' : 'Hide from inbox'}>
                          <Box flexShrink={0} onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={!!em.is_inbox_hidden}
                              style={{ cursor:'pointer', accentColor:'#718096', width:'13px', height:'13px' }}
                              onChange={async e => {
                                e.stopPropagation();
                                const hide = !em.is_inbox_hidden;
                                await api.patch('/email/messages/' + em.id + '/hide', { hidden: hide });
                                fetchEmails();
                              }} />
                          </Box>
                        </Tooltip>

                        <Box flex={1} minW={0} cursor="pointer"
                          onClick={() => {
                            // Optimistically mark as read in local state
                            setEmails(prev => prev.map(e =>
                              e.id === em.id ? { ...e, is_read: 1, inbox_status: e.inbox_status === 'shown' || !e.inbox_status ? 'viewed' : e.inbox_status } : e
                            ));
                            if (isAssignedUnseen && em.project_id && onNavigateToProject) {
                              onClose();
                              onNavigateToProject(em.project_id, em.row_group_id, em.task_id);
                            } else {
                              openEmail(em);
                            }
                          }}>
                          <Flex align="center" gap={2}>
                            {isUnread && !isAssignedUnseen && <Box w="6px" h="6px" borderRadius="full" bg="yellow.500" flexShrink={0} />}
                            {isAssignedUnseen && <Box w="6px" h="6px" borderRadius="full" bg="purple.500" flexShrink={0} />}
                            <Text fontSize="12px" fontWeight={isUnread ? '800' : '400'}
                              color={isUnread ? 'gray.900' : 'gray.700'} noOfLines={1}>{em.subject}</Text>
                            {isUnread && !isAssignedUnseen && <Badge fontSize="7px" colorScheme="yellow" flexShrink={0}>NEW</Badge>}
                          </Flex>
                          <HStack fontSize="10px" color="gray.400" spacing={2} flexWrap="wrap">
                            <Text noOfLines={1}>{em.from_name || em.from_address}</Text>
                            <Text flexShrink={0}>{fmtDate(em.received_at)}</Text>
                            {em.contact_first_name && <Badge fontSize="7px" colorScheme="gray" flexShrink={0}>{em.contact_first_name}</Badge>}
                            {isAssignedUnseen && em.project_name && (
                              <Badge fontSize="7px" colorScheme="purple" flexShrink={0}>
                                📁 {em.project_name}{em.task_title ? ` › ${em.task_title}` : ''}
                              </Badge>
                            )}
                            {!isAssignedUnseen && em.is_assigned && em.project_name && <Badge fontSize="7px" colorScheme="gray" variant="subtle" flexShrink={0}>→ {em.project_name}</Badge>}
                            {em.attachment_count > 0 && <Badge fontSize="7px" colorScheme="gray" flexShrink={0}><FiPaperclip size={8} /> {em.attachment_count}</Badge>}
                          </HStack>
                        </Box>
                        <HStack spacing={1} flexShrink={0}>
                          {isAssignedUnseen
                            ? <Tooltip label="Click to go to project task"><Badge fontSize="7px" colorScheme="purple" variant="solid" cursor="pointer"
                                onClick={() => { if (em.project_id && onNavigateToProject) { onClose(); onNavigateToProject(em.project_id, em.row_group_id, em.task_id); } }}>
                                → Task
                              </Badge></Tooltip>
                            : isUnread
                              ? <Badge fontSize="7px" colorScheme="yellow" variant="solid">NEW</Badge>
                              : <StatusBadge status={em.inbox_status} />
                          }
                          {!isAcked && !isAssignedUnseen && (
                            <Tooltip label="Acknowledge">
                              <IconButton size="xs" icon={<FiCheck size={10} />} variant="ghost" colorScheme="green"
                                aria-label="Acknowledge" onClick={e => handleAcknowledge(em.id, e)} />
                            </Tooltip>
                          )}
                          {isAssignedUnseen && (
                            <Tooltip label="Acknowledge (hide from inbox)">
                              <IconButton size="xs" icon={<FiCheck size={10} />} variant="ghost" colorScheme="purple"
                                aria-label="Acknowledge assigned" onClick={async e => { e.stopPropagation(); await handleAcknowledge(em.id, e); }} />
                            </Tooltip>
                          )}
                        </HStack>
                      </Flex>
                    );
                  })}
                </VStack>
              )
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>

    {/* Stage 1 — Add rule prompt */}
    <AlertDialog isOpen={rulePromptOpen} leastDestructiveRef={rulePromptRef}
      onClose={() => { setRulePromptOpen(false); setPendingAssign(null); }}>
      <AlertDialogOverlay />
      <AlertDialogContent>
        <AlertDialogHeader fontSize="md">Add Email Filter Rule?</AlertDialogHeader>
        <AlertDialogBody>
          <Text fontSize="sm">Automatically route future emails from <strong>{pendingAssign && pendingAssign.from_address}</strong> to this task?</Text>
          <Text fontSize="xs" color="gray.500" mt={2}>This creates a rule under Settings / Email Rules. Manage rules from the inbox via the filter icon.</Text>
        </AlertDialogBody>
        <AlertDialogFooter>
          <Button ref={rulePromptRef} size="sm" variant="ghost" onClick={handleSkipRule}>No, Skip</Button>
          <Button size="sm" colorScheme="blue" ml={3} onClick={handleCreateRule}>Yes, Add Rule</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Stage 2 — Apply rule to all existing emails? */}
    <AlertDialog isOpen={applyAllPromptOpen} leastDestructiveRef={applyAllPromptRef}
      onClose={handleApplyAllSkip}>
      <AlertDialogOverlay />
      <AlertDialogContent>
        <AlertDialogHeader fontSize="md">Apply Rule to Existing Inbox?</AlertDialogHeader>
        <AlertDialogBody>
          <Text fontSize="sm">Do you want to run this filter rule on all emails currently in the inbox?</Text>
          <Text fontSize="xs" color="gray.500" mt={2}>Matching emails will be assigned to the same project task automatically.</Text>
        </AlertDialogBody>
        <AlertDialogFooter>
          <Button size="sm" variant="ghost" onClick={handleApplyAllSkip}>No, Just This Email</Button>
          <Button size="sm" colorScheme="blue" ml={3} isLoading={applyingAll} onClick={handleApplyAll}>Yes, Apply to All</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* History date-range modal */}
    {historyOpen && (
      <Modal isOpen={historyOpen} onClose={() => { setHistoryOpen(false); setHistoryEmails([]); }} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader fontSize="md">
            <HStack spacing={2}>
              <FiClock />
              <Text>Old Messages</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack spacing={3} mb={4} align="flex-end">
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>From</Text>
                <Input size="sm" type="date" value={historyFrom} onChange={e => setHistoryFrom(e.target.value)} w="150px" />
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>To</Text>
                <Input size="sm" type="date" value={historyTo} onChange={e => setHistoryTo(e.target.value)} w="150px" />
              </Box>
              <Button size="sm" colorScheme="blue" onClick={fetchHistory} isLoading={historyLoading}>
                Load Messages
              </Button>
            </HStack>
            {historyEmails.length === 0 && !historyLoading && (
              <Text fontSize="sm" color="gray.400" textAlign="center" py={6}>
                Select a date range and click Load Messages
              </Text>
            )}
            {historyEmails.length > 0 && (
              <VStack spacing={1} align="stretch">
                <Text fontSize="10px" color="gray.400" mb={1}>{historyEmails.length} messages</Text>
                {historyEmails.map(em => (
                  <Flex key={em.id} p={2} border="1px" borderRadius="md" borderColor="gray.100"
                    bg={em.inbox_status === 'acknowledged' ? 'gray.50' : em.is_assigned ? 'teal.50' : 'white'}
                    align="center" justify="space-between" gap={2}>
                    <Box flex={1} minW={0}>
                      <Flex align="center" gap={2}>
                        <Text fontSize="12px" fontWeight="500" noOfLines={1}>{em.subject || '(no subject)'}</Text>
                        {em.is_assigned && <Badge fontSize="7px" colorScheme="gray" variant="outline">Sent to task</Badge>}
                        {em.inbox_status === 'acknowledged' && <Badge fontSize="7px" colorScheme="green" variant="outline">Acknowledged</Badge>}
                      </Flex>
                      <HStack fontSize="10px" color="gray.400" spacing={2}>
                        <Text noOfLines={1}>{em.from_name || em.from_address}</Text>
                        <Text flexShrink={0}>{fmtDate(em.received_at)}</Text>
                        {em.project_name && <Badge fontSize="7px" colorScheme="teal" variant="subtle">→ {em.project_name}</Badge>}
                      </HStack>
                    </Box>
                    <HStack spacing={1} flexShrink={0}>
                      {em.inbox_status !== 'acknowledged' && (
                        <Tooltip label="Acknowledge">
                          <IconButton size="xs" icon={<FiCheck size={10} />} variant="ghost" colorScheme="green"
                            onClick={() => handleAcknowledge(em.id)} />
                        </Tooltip>
                      )}
                    </HStack>
                  </Flex>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={() => { setHistoryOpen(false); setHistoryEmails([]); }}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )}

    {/* Right-click context menu */}
    {contextMenu && (
      <>
        <Box position="fixed" inset={0} zIndex={1500} onClick={() => setContextMenu(null)} />
        <Box ref={contextMenuRef}
          position="fixed" top={contextMenuPos.top} left={contextMenuPos.left} zIndex={1501}
          bg="white" border="1px" borderColor="gray.200" borderRadius="md" shadow="lg" py={1} minW="220px"
          maxH={contextMenuPos.maxHeight} maxW={contextMenuPos.maxWidth} overflowY="auto" overflowX="hidden"
          visibility={contextMenuPos.ready ? 'visible' : 'hidden'}>
          {contextMenu.isHidden ? (
            <Box px={3} py={1.5} cursor="pointer" fontSize="12px" _hover={{ bg: 'gray.50' }}
              onClick={() => { api.patch('/email/messages/' + contextMenu.emailId + '/hide', { hidden: false }).then(() => { fetchEmails(); setContextMenu(null); }); }}>
              👁 Show in inbox
            </Box>
          ) : (
            <Box px={3} py={1.5} cursor="pointer" fontSize="12px" _hover={{ bg: 'gray.50' }}
              onClick={() => handleHideEmail(contextMenu.emailId)}>
              🙈 Hide from inbox
            </Box>
          )}
          {contextMenu.isAssigned && (
            <>
              <Box h="1px" bg="gray.100" my={1} />
              <Box px={3} py={1.5} cursor="pointer" fontSize="12px" color="orange.600" _hover={{ bg: 'orange.50' }}
                onClick={async () => {
                  try {
                    await api.patch('/email/messages/' + contextMenu.emailId + '/unassign');
                    await fetchEmails();
                    if (onRefreshCounts) onRefreshCounts();
                    toast({ title: 'Returned to inbox', status: 'info', duration: 2000 });
                  } catch(e) { toast({ title: 'Failed to undo', status: 'error', duration: 2000 }); }
                  setContextMenu(null);
                }}>
                ↩ Undo send to task
              </Box>
            </>
          )}
          <Box h="1px" bg="gray.100" my={1} />
          {contextMenu.isAssigned ? (
            <Box px={3} py={1.5} cursor="pointer" fontSize="12px" color="red.600" _hover={{ bg: 'red.50' }}
              onClick={() => handleDeleteEmail(contextMenu.emailId)}>
              🗑 Delete message
            </Box>
          ) : (
            <Tooltip label="You can't delete messages unless they've been sent to a project task first" placement="right">
              <Box px={3} py={1.5} fontSize="12px" color="gray.300" cursor="not-allowed">
                🗑 Delete message
              </Box>
            </Tooltip>
          )}
        </Box>
      </>
    )}
    </>
  );
}
